/**
 * Grade-monoid arithmetic for the SDP-adjunction grade monoid
 * `G = ⊕_{s ∈ SDP} ℤ/2[Φ(s)]`.
 *
 * This is the algorithmic core that powers the interactive Bridge
 * Constructor, the Composition Playground, and the SNF Walker. The
 * functions here are pure, total, and deterministic: no I/O, no side
 * effects, no exceptions. A grade is a `Readonly<Record<string, 0|1>>`
 * keyed by `${sdp}.${phase}`; reads default missing keys to `0`, so
 * partial inputs behave as if the missing components were neutral.
 *
 * Canonical authority: theory/manuscripts/contrib/TwoCategoricalCSK/
 * 00-Mathematical-Foundations.md §3 and 03-SDP-Typing-Kit.md §3.4.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  COMPOSITIONALITY_PRINCIPLE_CODES,
  getImmunityClosureGuaranteeNameForSdp,
} from "@csk/core";
import type {
  CompositionalityPrincipleCode,
  ImmunityClosureGuaranteeName,
} from "@csk/core";

/** The seven SDP codes that index G's components. */
export const SDP_CODES = COMPOSITIONALITY_PRINCIPLE_CODES;

/** The two adjunction phases per SDP. */
export const PHASES = ["eta", "epsilon"] as const;

export type SdpCode = CompositionalityPrincipleCode;
export type Phase = (typeof PHASES)[number];

/**
 * A grade vector — fourteen ℤ/2 components, one per (SDP, phase)
 * pair. Stored as an immutable record from `${sdp}.${phase}` →
 * 0 | 1.
 */
export type Grade = Readonly<Record<string, 0 | 1>>;

const KEY_SEPARATOR = "." as const;

/** Build the canonical key for a grade component. */
export function componentKey(sdp: SdpCode, phase: Phase): string {
  return `${sdp}${KEY_SEPARATOR}${phase}`;
}

/** The neutral grade `0_G`. */
export function neutralGrade(): Grade {
  const out: Record<string, 0 | 1> = {};
  for (const sdp of SDP_CODES) {
    for (const phase of PHASES) {
      out[componentKey(sdp, phase)] = 0;
    }
  }
  return Object.freeze(out);
}

/** A unit grade vector with a single component lit. */
export function singletonGrade(sdp: SdpCode, phase: Phase): Grade {
  const base = { ...neutralGrade() } as Record<string, 0 | 1>;
  base[componentKey(sdp, phase)] = 1;
  return Object.freeze(base);
}

/** Equality (component-wise) on grades. */
export function gradeEquals(a: Grade, b: Grade): boolean {
  for (const sdp of SDP_CODES) {
    for (const phase of PHASES) {
      const key = componentKey(sdp, phase);
      if ((a[key] ?? 0) !== (b[key] ?? 0)) {
        return false;
      }
    }
  }
  return true;
}

/** Addition in G — componentwise ℤ/2. */
export function gradeAdd(a: Grade, b: Grade): Grade {
  const out: Record<string, 0 | 1> = {};
  for (const sdp of SDP_CODES) {
    for (const phase of PHASES) {
      const key = componentKey(sdp, phase);
      const sum = (a[key] ?? 0) ^ (b[key] ?? 0);
      out[key] = sum === 1 ? 1 : 0;
    }
  }
  return Object.freeze(out);
}

/** Whether a grade is the neutral grade. */
export function isNeutral(grade: Grade): boolean {
  return gradeEquals(grade, neutralGrade());
}

/** Pretty-print a single grade component. */
export function formatComponent(sdp: SdpCode, phase: Phase): string {
  const phaseSymbol = phase === "eta" ? "η" : "ε";
  return `[${sdp}, ${phaseSymbol}]`;
}

/** Pretty-print a grade as a sum of components, or "0_G". */
export function formatGrade(grade: Grade): string {
  const parts: string[] = [];
  for (const sdp of SDP_CODES) {
    for (const phase of PHASES) {
      if ((grade[componentKey(sdp, phase)] ?? 0) === 1) {
        parts.push(formatComponent(sdp, phase));
      }
    }
  }
  return parts.length === 0 ? "0_G" : parts.join(" + ");
}

/**
 * `sdpIndex : G → 𝒫(SDP)` — the set of SDPs with any non-zero
 * component. Used for T4 phase-index diagonality checks (B2 and B4
 * must agree on `sdpIndex`).
 */
export function sdpIndex(grade: Grade): ReadonlySet<SdpCode> {
  const out = new Set<SdpCode>();
  for (const sdp of SDP_CODES) {
    for (const phase of PHASES) {
      if ((grade[componentKey(sdp, phase)] ?? 0) === 1) {
        out.add(sdp);
        break;
      }
    }
  }
  return out;
}

/** Equality on sdpIndex sets (used for T4 checks). */
export function sdpIndexEquals(
  a: ReadonlySet<SdpCode>,
  b: ReadonlySet<SdpCode>,
): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/**
 * Identify the ICG envelope grade for SDP `s`: `[s, η] + [s, ε]`.
 * The seven ICGs (ICG-Tr through ICG-Pa) live on these seven
 * diagonals of G.
 */
export function icgEnvelope(sdp: SdpCode): Grade {
  return gradeAdd(singletonGrade(sdp, "eta"), singletonGrade(sdp, "epsilon"));
}

export interface IcgEnvelopeDetection {
  readonly sdp: SdpCode;
  readonly icg: ImmunityClosureGuaranteeName;
}

/**
 * If `grade` equals an ICG envelope, return the typed SDP and ICG
 * identifiers; otherwise `null`. Used by the Composition Playground to
 * identify Case-B composites (chapter 5 §5.3).
 */
export function detectIcgEnvelope(grade: Grade): IcgEnvelopeDetection | null {
  for (const sdp of SDP_CODES) {
    if (gradeEquals(grade, icgEnvelope(sdp))) {
      return { sdp, icg: getImmunityClosureGuaranteeNameForSdp(sdp) };
    }
  }
  return null;
}
